import styled from "styled-components";
import defaultTheme from "../../../theme/theme";
import { TypeColorNames } from "../../../theme/colors";
import { notification } from "antd";
import { t } from "i18next";

type IconType =
  | "facebook"
  | "twitter"
  | "linkedin"
  | "url";

interface StyledSocialIconsProps {
  type: IconType;
  color: string;
  style?: any;
  theme: typeof defaultTheme;
  background: TypeColorNames;
}

export interface IIconShare {
  color: TypeColorNames;
  type: IconType;
  url: string; 
  style?: any;
  background: TypeColorNames;
}

const DEFAULT_COLOR = "darkBackground";
const DEFAULT_BACKGROUND = "offWhite";
const DEFAULT_IS_ACTIVE = true;

const getBackgroundColor = ({
  background,
  theme,
}: StyledSocialIconsProps): string => theme?.colors[background];

const StyledSocialIcons = styled.a<StyledSocialIconsProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${getBackgroundColor};
  height: 32px;
  width: 32px;
  border-radius: 50%;
  &:hover {
    transform: scale(1.1);
    transition: 0.3s;
  }
`;

const share = (url: string, type: IconType) => {
  switch(type){
    case 'facebook': 
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, 'width=550,height=450');
      break; 
    case 'twitter': 
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, 'width=550,height=450');
      break;  
    case 'linkedin': 
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, 'width=550,height=450');
      break; 
    case 'url':
    default: 
      navigator.clipboard.writeText(url);
      notification.success({ message: t('talk.copyLink') });
      break; 
  }
}

export const IconShare = ({
  color,
  type,
  url,
  style,
  background,
}: IIconShare) => {

  return (
    <>
        <StyledSocialIcons
          onClick={() => {share(url, type)}}
          target="blank"
          background={background}
          color={color}
          type={type}
          style={{ ...style }}
        >
          {type === "facebook" && (
            <svg 
              width="41" 
              height="40" 
              viewBox="0 0 41 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >s
            <circle cx="20.5716" cy="19.6429" r="19.6429" fill={color}/>
            <path d="M21.4566 28.2363V20.3963H24.0881L24.4822 17.3408H21.4566V15.3901C21.4566 14.5054 21.7022 13.9026 22.9708 13.9026L24.5886 13.9018V11.1691C24.3087 11.1319 23.3485 11.0488 22.2311 11.0488C19.8984 11.0488 18.3014 12.4726 18.3014 15.0876V17.3408H15.6631V20.3963H18.3014V28.2363H21.4566Z" fill="white"/>
            </svg>
            
          )}

          {type === "twitter" && (
            <svg 
              width="40" 
              height="40" 
              viewBox="0 0 40 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
            <circle cx="19.8572" cy="19.6429" r="19.6429" fill={color}/>
            <path d="M29.9292 13.8938C29.1932 14.2149 28.4027 14.4321 27.5726 14.53C28.4198 14.03 29.0704 13.2386 29.3767 12.2954C28.584 12.7583 27.706 13.0944 26.7712 13.2757C26.0227 12.4908 24.9565 12 23.7761 12C21.5099 12 19.6728 13.8085 19.6728 16.0391C19.6728 16.3557 19.7089 16.6639 19.779 16.9596C16.3689 16.7909 13.3455 15.183 11.3217 12.7392C10.9684 13.3358 10.7661 14.0297 10.7661 14.7698C10.7661 16.171 11.4906 17.4075 12.5915 18.132C11.9187 18.1111 11.2862 17.9292 10.7328 17.6268C10.7325 17.6436 10.7325 17.6605 10.7325 17.6777C10.7325 19.6347 12.1468 21.2674 14.0239 21.6383C13.6796 21.7306 13.3172 21.78 12.9429 21.78C12.6784 21.78 12.4216 21.7545 12.1708 21.7076C12.693 23.3125 14.2083 24.4802 16.0037 24.5127C14.5994 25.5961 12.8301 26.2418 10.9079 26.2418C10.5767 26.2418 10.2501 26.2228 9.9292 26.1853C11.7452 27.3313 13.9017 28 16.219 28C23.7662 28 27.8936 21.8453 27.8936 16.5075C27.8936 16.3323 27.8895 16.1581 27.8817 15.9851C28.6834 15.4152 29.3792 14.7039 29.9292 13.8938Z" fill="white"/>
            </svg>
            
          )}

          {type === "linkedin" && (
            <svg 
              width="40" 
              height="40" 
              viewBox="0 0 40 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
            <circle cx="20.1429" cy="19.6429" r="19.6429" fill={color}/>
            <path fillRule="evenodd" clipRule="evenodd" d="M15.7735 27.1843V16.2641H12.1441V27.184H15.7735V27.1843ZM13.9588 14.7738C15.2242 14.7738 16.0125 13.9346 16.0125 12.8868C15.9887 11.8158 15.2242 11 13.9831 11C12.7412 11 11.9297 11.8155 11.9297 12.8868C11.9297 13.9346 12.7174 14.7738 13.9356 14.7738H13.9588Z" fill="white"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M17.7812 27.2366H21.4112V21.1386C21.4112 20.8123 21.4347 20.486 21.5308 20.2529C21.7927 19.6008 22.3901 18.9249 23.3927 18.9249C24.7062 18.9249 25.2317 19.9268 25.2317 21.3946V27.2366H28.8611V20.9751C28.8611 17.6211 27.0702 16.0601 24.6819 16.0601C22.7241 16.0601 21.8646 17.1549 21.3869 17.9001H21.4112V16.3164H17.7812C17.829 17.341 17.7812 27.2366 17.7812 27.2366Z" fill="white"/>
            </svg>
            
          )}

          {type === "url" && 
            <svg
            width="41"
            height="40"
            viewBox="0 0 41 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
              <circle cx="20.4287" cy="19.6429" r="19.6429" fill={color}/>
              <path fillRule="evenodd" clipRule="evenodd" d="M28.6309 12.3293C26.7921 10.4906 23.8109 10.4906 21.9722 12.3293L19.0635 15.238C18.8107 15.4908 19.0192 15.8828 19.3764 15.8674L20.8853 15.8027C21.1823 15.79 21.4637 15.6663 21.6739 15.4561L23.3864 13.7436C24.4441 12.6859 26.1589 12.6859 27.2166 13.7436C28.2743 14.8012 28.2743 16.5161 27.2166 17.5738L24.308 20.4825C23.2503 21.5402 21.5354 21.5402 20.4777 20.4825C20.2543 20.259 19.9617 20.1002 19.646 20.1138L19.5222 20.1191C18.7538 20.152 18.2486 20.9335 18.7334 21.5306C18.8359 21.6569 18.9459 21.7791 19.0635 21.8967C20.9022 23.7354 23.8834 23.7354 25.7222 21.8967L28.6309 18.988C30.4696 17.1493 30.4696 14.1681 28.6309 12.3293Z" fill="white"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M22.198 19.7428C23.0172 19.7149 23.5354 18.8664 22.974 18.2691C22.9398 18.2326 22.9048 18.1966 22.8692 18.161C21.0045 16.2963 17.9813 16.2963 16.1166 18.161L13.3019 20.9757C11.4372 22.8404 11.4372 25.8637 13.3018 27.7283C15.1665 29.593 18.1898 29.593 20.0544 27.7283L22.8692 24.9135C23.0657 24.7171 22.9087 24.4065 22.6311 24.416L20.9191 24.4742C20.6271 24.4842 20.3497 24.6046 20.1431 24.8112L18.6402 26.3141C17.5566 27.3977 15.7997 27.3977 14.7161 26.3141C13.6324 25.2305 13.6324 23.4736 14.7161 22.39L17.5308 19.5752C18.6145 18.4915 20.3714 18.4915 21.455 19.5752C21.5681 19.6883 21.7194 19.759 21.8793 19.7536L22.198 19.7428Z" fill="white"/>
            </svg>
          }

        </StyledSocialIcons>
    </>
  );
};

IconShare.defaultProps = {
  color: DEFAULT_COLOR,
  background: DEFAULT_BACKGROUND,
  isActive: DEFAULT_IS_ACTIVE,
};

